// src/components/circles/CircleFormFields.tsx
//
// Shared form fields for creating/editing a Circle. Owns:
//   name / description / type / icon / color / visibility / ownerUserId /
//   parentCircleId
//
// Does NOT own membership editing — that lives in the Circle detail
// "People" tab so a Circle can be created empty and populated later.
// Progressive disclosure: Membership (manual / rules / Central team) first,
// then the kinds that are real for it, then visibility in plain words. Owner
// is always shown (it defaults to the creator); Parent appears only when
// another Circle exists to choose. "Rules" makes a Smart Circle: members are
// the contacts matching every rule (SmartRulesBuilder), evaluated per viewer.
// Restricted visibility has no behaviour behind it and is not offered for new
// Circles — the server refuses it — but a Circle that already has it keeps it.

import { useMemo } from 'react';
import type {
  CentralTeamSummary,
  CircleType,
  CircleVisibility,
  Circle,
  ContactFilterError,
} from '../../types/api';
import { CIRCLE_TYPE_LABELS, isCentralTeamBackedCircleType } from './circleDisplay';
import SmartRulesBuilder from './SmartRulesBuilder';
import { filterToDrafts, newDraft, type SmartRuleDraft } from './smartRules';

export interface CircleFormValues {
  name: string;
  description: string;
  type: CircleType;
  icon: string;
  color: string;
  visibility: CircleVisibility;
  ownerUserId: string;
  parentCircleId: string;
  centralTeamId: string;
  /** Smart Circle rule rows (type 'smart_segment' only). */
  smartRules?: SmartRuleDraft[];
}

export interface CircleOwnerCandidate {
  id: string;
  fullName: string | null;
  email: string | null;
}

/**
 * Where the owner picker's list stands. `unavailable` = the caller cannot read
 * the workspace member list (GET /api/team is gated by the Central `team.read`
 * capability), so the owner is shown but cannot be changed here.
 */
export type OwnerCandidatesStatus = 'loading' | 'ready' | 'unavailable';

interface CircleFormFieldsProps {
  values: CircleFormValues;
  onChange: (values: CircleFormValues) => void;
  candidateParents?: Circle[];
  /**
   * Owner picker source: CURRENT workspace members, from the same roster
   * (GET /api/team → user_workspace_roles) the server validates an owner
   * against. Never a free-text id.
   */
  workspaceMembers?: CircleOwnerCandidate[];
  ownerCandidatesStatus?: OwnerCandidatesStatus;
  /** The signed-in user — labelled "(you)" in the picker. */
  currentUserId?: string | null;
  /**
   * Central Team dropdown source. Ordered active-first by convention.
   * Undefined = picker not shown (loading or SDK unavailable).
   */
  centralTeams?: CentralTeamSummary[];
  /**
   * When true, the Central Team select is rendered but disabled with a
   * "not configured" hint. Distinct from `disabled` (the form-wide flag).
   */
  centralTeamsUnavailable?: boolean;
  disabled?: boolean;
  /** Field-level Smart Circle rule errors (400 INVALID_SMART_RULES / submit check). */
  ruleErrors?: ContactFilterError[];
  /** Live preview of matching contacts in the rule builder. Default on. */
  smartPreview?: boolean;
}

// Kind names come from the same map the Circle badges use
// (CIRCLE_TYPE_LABELS), so a Circle created as one kind is not shown back
// under another name.

const VISIBILITY_LABEL: Record<CircleVisibility, string> = {
  workspace: 'Workspace',
  private: 'Private',
  restricted: 'Restricted (works like Private)',
};

const VISIBILITY_HELP: Record<CircleVisibility, string> = {
  workspace: 'Everyone in the workspace can find and use this Circle.',
  private: 'Only you, the owner and the Circle’s members can see it.',
  restricted:
    'Restricted has no audience of its own yet: it works exactly like Private (you, the owner and members).',
};

export const DEFAULT_CIRCLE_FORM_VALUES: CircleFormValues = {
  name: '',
  description: '',
  type: 'custom',
  icon: '',
  color: '',
  visibility: 'workspace',
  ownerUserId: '',
  parentCircleId: '',
  centralTeamId: '',
};

export function circleToFormValues(circle: Circle): CircleFormValues {
  return {
    name: circle.name,
    description: circle.description ?? '',
    type: circle.type,
    icon: circle.icon ?? '',
    color: circle.color ?? '',
    visibility: circle.visibility,
    ownerUserId: circle.ownerUserId ?? '',
    parentCircleId: circle.parentCircleId ?? '',
    centralTeamId: circle.centralTeamId ?? '',
    smartRules: filterToDrafts(circle.smartRules ?? null),
  };
}

export default function CircleFormFields({
  values,
  onChange,
  candidateParents = [],
  workspaceMembers = [],
  ownerCandidatesStatus = 'ready',
  currentUserId = null,
  centralTeams,
  centralTeamsUnavailable = false,
  disabled,
  ruleErrors,
  smartPreview = true,
}: CircleFormFieldsProps) {
  const set = <K extends keyof CircleFormValues>(
    key: K,
    v: CircleFormValues[K],
  ) => {
    // Switching to a Circle type that cannot back a Central Team must
    // clear any previously-selected centralTeamId in the SAME state
    // update so the submit body carries `centralTeamId: null` and no
    // stale link is preserved behind a hidden field.
    if (key === 'type' && !isCentralTeamBackedCircleType(v as CircleType)) {
      onChange({ ...values, type: v as CircleType, centralTeamId: '' });
      return;
    }
    onChange({ ...values, [key]: v });
  };

  const showCentralTeamField = isCentralTeamBackedCircleType(values.type);
  const isSmart = values.type === 'smart_segment';
  const membership: 'manual' | 'central' | 'rules' = showCentralTeamField
    ? 'central'
    : isSmart
      ? 'rules'
      : 'manual';

  const setMembership = (m: string) => {
    if (m === 'rules') {
      onChange({
        ...values,
        type: 'smart_segment',
        centralTeamId: '',
        // Start with one empty rule so the builder is ready to fill in.
        smartRules: values.smartRules && values.smartRules.length > 0 ? values.smartRules : [newDraft()],
      });
      return;
    }
    set('type', m === 'central' ? 'team' : 'custom');
  };

  // Kinds valid for the current membership source. Legacy values a Circle
  // already carries stay selectable for THAT Circle (so a save never changes
  // them behind the user's back) but are not offered to anything else.
  const kindOptions = useMemo<CircleType[]>(() => {
    const base: CircleType[] =
      membership === 'central'
        ? ['team', 'department']
        : membership === 'rules'
          ? ['smart_segment']
          : ['custom', 'territory', 'account_team'];
    if (!base.includes(values.type)) base.push(values.type);
    return base;
  }, [membership, values.type]);

  const visibilityOptions = useMemo<CircleVisibility[]>(() => {
    const base: CircleVisibility[] = ['workspace', 'private'];
    if (!base.includes(values.visibility)) base.push(values.visibility);
    return base;
  }, [values.visibility]);

  // Owner choices: current members by human-readable name. The stored owner is
  // ALWAYS representable — if they are not in the list (left the workspace, or
  // the list is unavailable) they appear under a descriptive label, never as a
  // raw id, so a save never silently changes the owner.
  const ownerOptions = useMemo(() => {
    const opts = [...workspaceMembers]
      .map((m) => ({
        id: m.id,
        label:
          (m.fullName?.trim() || m.email?.trim() || 'Unnamed member') +
          (currentUserId && m.id === currentUserId ? ' (you)' : ''),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (values.ownerUserId && !opts.some((o) => o.id === values.ownerUserId)) {
      opts.unshift({
        id: values.ownerUserId,
        label:
          values.ownerUserId === currentUserId
            ? 'You'
            : ownerCandidatesStatus === 'ready'
              ? 'Current owner (no longer a workspace member)'
              : 'Current owner',
      });
    }
    return opts;
  }, [workspaceMembers, currentUserId, values.ownerUserId, ownerCandidatesStatus]);
  const ownerOrphaned =
    ownerCandidatesStatus === 'ready' &&
    !!values.ownerUserId &&
    !workspaceMembers.some((m) => m.id === values.ownerUserId);

  const parentOptions = useMemo(
    () => candidateParents.filter((c) => !c.archivedAt),
    [candidateParents],
  );

  // Sort teams: active first, then alphabetical; keep the currently-linked
  // team visible even if it's since been archived at Central so the user
  // can still see & unlink it.
  const teamOptions = useMemo(() => {
    if (!centralTeams) return [];
    const sorted = [...centralTeams].sort((a, b) => {
      if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    if (
      values.centralTeamId &&
      !sorted.some((t) => t.id === values.centralTeamId)
    ) {
      sorted.push({
        id: values.centralTeamId,
        name: 'Unknown team',
        description: null,
        type: 'custom',
        status: 'archived',
      });
    }
    return sorted;
  }, [centralTeams, values.centralTeamId]);

  const inputCls =
    'block w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-900 placeholder:text-slate-400 shadow-sm transition focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/40 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500';
  const labelCls = 'block text-[11px] font-medium text-slate-700 mb-1';

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor="circle-name" className={labelCls}>
          Name <span className="text-red-500">*</span>
        </label>
        <input
          id="circle-name"
          type="text"
          value={values.name}
          onChange={(e) => set('name', e.target.value)}
          className={inputCls}
          placeholder="e.g., Care Team, VIP Clients"
          required
          disabled={disabled}
          autoFocus
        />
      </div>

      <div>
        <label htmlFor="circle-description" className={labelCls}>
          Description <span className="ml-1 font-normal text-slate-400">— optional</span>
        </label>
        <textarea
          id="circle-description"
          value={values.description}
          onChange={(e) => set('description', e.target.value)}
          rows={2}
          className={`${inputCls} resize-none`}
          placeholder="What is this circle for?"
          disabled={disabled}
        />
      </div>

      {/*
        MEMBERSHIP FIRST. Of the seven stored types only two behave
        differently: `team` and `department` are projections of a Central Team
        (Central owns the roster; CRM reads it live). Every other type is a
        manual Circle with a label. So the form asks where membership comes
        from, then offers only the kinds that are real for that source.
      */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="circle-membership" className={labelCls}>Membership</label>
          <select
            id="circle-membership"
            value={membership}
            onChange={(e) => setMembership(e.target.value)}
            className={inputCls}
            disabled={disabled}
            aria-describedby="circle-membership-help"
          >
            <option value="manual">Manual members</option>
            <option value="rules">Rules (Smart Circle)</option>
            <option value="central">Central team</option>
          </select>
        </div>
        <div className={isSmart ? 'hidden' : undefined}>
          <label htmlFor="circle-type" className={labelCls}>Kind</label>
          <select
            id="circle-type"
            value={values.type}
            onChange={(e) => set('type', e.target.value as CircleType)}
            className={inputCls}
            disabled={disabled}
          >
            {kindOptions.map((t) => (
              <option key={t} value={t}>{CIRCLE_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </div>
      </div>
      <p id="circle-membership-help" className="-mt-1.5 text-[11px] text-slate-500">
        {membership === 'central'
          ? 'Members come from a Central team and stay in sync. Membership is managed in Central, not here.'
          : membership === 'rules'
            ? 'Members are the contacts that match your rules. Nobody is added or removed by hand.'
            : 'You add and remove members yourself. The kind is a label for finding and filtering Circles.'}
      </p>

      {isSmart && (
        <SmartRulesBuilder
          drafts={values.smartRules ?? []}
          onChange={(smartRules) => onChange({ ...values, smartRules })}
          errors={ruleErrors}
          members={workspaceMembers}
          membersStatus={ownerCandidatesStatus}
          currentUserId={currentUserId}
          disabled={disabled}
          preview={smartPreview}
        />
      )}

      <div>
        <label htmlFor="circle-visibility" className={labelCls}>Visibility</label>
        <select
          id="circle-visibility"
          value={values.visibility}
          onChange={(e) => set('visibility', e.target.value as CircleVisibility)}
          className={inputCls}
          disabled={disabled}
          aria-describedby="circle-visibility-help"
        >
          {visibilityOptions.map((v) => (
            <option key={v} value={v}>{VISIBILITY_LABEL[v]}</option>
          ))}
        </select>
        <p id="circle-visibility-help" className="mt-1 text-[11px] text-slate-500">
          {VISIBILITY_HELP[values.visibility]} Seeing a Circle never gives access to the contacts,
          deals or files in it — those keep their own permissions.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="circle-icon" className={labelCls}>Icon</label>
          <input
            id="circle-icon"
            type="text"
            value={values.icon}
            onChange={(e) => set('icon', e.target.value)}
            className={inputCls}
            placeholder="🫂 or team"
            disabled={disabled}
          />
        </div>
        <div>
          <label htmlFor="circle-color" className={labelCls}>Color</label>
          <div className="flex items-center gap-1.5">
            <label
              htmlFor="circle-color-picker"
              className="relative inline-flex h-7 w-7 shrink-0 cursor-pointer overflow-hidden rounded-md border border-slate-300 shadow-sm hover:border-slate-400"
              style={{ backgroundColor: values.color || '#6366f1' }}
              aria-label="Pick color"
            >
              <input
                id="circle-color-picker"
                type="color"
                value={values.color || '#6366f1'}
                onChange={(e) => set('color', e.target.value)}
                disabled={disabled}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              />
            </label>
            <input
              id="circle-color"
              type="text"
              value={values.color}
              onChange={(e) => set('color', e.target.value)}
              className={`${inputCls} font-mono`}
              placeholder="#6366f1"
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {/*
        OWNER is responsibility metadata: who looks after this Circle. It
        defaults to the creator and may only be a CURRENT workspace member
        (the server refuses anyone else with 400 INVALID_OWNER). It makes a
        Private Circle visible to that person — and that is all: no Circle
        edit rights, and never access to the records the Circle groups.
        Parent is navigation only — nothing is inherited through it — and is
        shown only when another Circle exists to choose.
      */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="min-w-0">
          <label htmlFor="circle-owner" className={labelCls}>Owner</label>
          <select
            id="circle-owner"
            value={values.ownerUserId}
            onChange={(e) => set('ownerUserId', e.target.value)}
            className={inputCls}
            disabled={disabled || ownerCandidatesStatus !== 'ready'}
            aria-describedby="circle-owner-help"
            data-testid="circle-owner-select"
          >
            {!values.ownerUserId && (
              <option value="">
                {ownerCandidatesStatus === 'loading' ? 'Loading members…' : 'You (creator)'}
              </option>
            )}
            {ownerOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
          <p id="circle-owner-help" className="mt-1 text-[11px] text-slate-500">
            The person responsible for this Circle. The owner can always see it, even when
            it’s Private. Being owner doesn’t give edit rights or access to the contacts,
            deals or files in the Circle.
            {ownerCandidatesStatus === 'unavailable' &&
              ' You can’t view the workspace member list, so the owner can’t be changed here.'}
            {ownerOrphaned && ' The current owner is no longer a workspace member — choose someone who is.'}
          </p>
        </div>
        {parentOptions.length > 0 && (
          <div className="min-w-0">
            <label htmlFor="circle-parent" className={labelCls}>Parent Circle</label>
            <select
              id="circle-parent"
              value={values.parentCircleId}
              onChange={(e) => set('parentCircleId', e.target.value)}
              className={inputCls}
              disabled={disabled}
              aria-describedby="circle-parent-help"
            >
              <option value="">None</option>
              {parentOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <p id="circle-parent-help" className="mt-1 text-[11px] text-slate-500">
              For organizing only — nothing is inherited from the parent.
            </p>
          </div>
        )}
      </div>

      {showCentralTeamField && (
        <div data-testid="circle-central-team-field">
          <label htmlFor="circle-central-team" className={labelCls}>
            Team
            {/* Phase 12.1 — REQUIRED for department/team. Callers must
                be the Head of the chosen Central Team; backend rejects
                an empty selection with 400 CENTRAL_TEAM_REQUIRED. */}
            <span className="ml-1 font-normal text-red-500">*</span>
          </label>
          <select
            id="circle-central-team"
            value={values.centralTeamId}
            onChange={(e) => set('centralTeamId', e.target.value)}
            className={inputCls}
            disabled={disabled || centralTeamsUnavailable}
            data-testid="circle-central-team-select"
            required
          >
            <option value="">Select a Central Team you head…</option>
            {teamOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.status === 'archived' ? ' (archived)' : ''}
              </option>
            ))}
          </select>
          {centralTeamsUnavailable && (
            <p className="mt-1 text-[11px] text-slate-500">
              Central Team service isn&apos;t configured for this environment. Link is disabled.
            </p>
          )}
          {!centralTeamsUnavailable && teamOptions.length === 0 && (
            <p
              className="mt-1 text-[11px] text-slate-600"
              data-testid="circle-central-team-head-required"
            >
              You must be the Head of a Central Team to create a Department or Team Circle.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
